import { useCallback, useMemo } from 'react';
import { ISlider } from './interfaces';

export const useSlider = ({
  singleSlideWidth,
  visibleSliderWidth,
  margin,
  activeSlide,
  childrenCount,
  sliderConstraints,
  trueSliderWidth,
  x,
  setActiveSlide,
}: ISlider) => {
  const remToPxValue = useMemo(() => {
    return margin * 16
  }, [margin]);

  // returns calculated shift for just for 2nd element
  // calculate how much width of 1st element is hidden to center 2nd element
  // that value is used to inifital shift
  const offsetFirstElement = useMemo(() => {
    return singleSlideWidth - (visibleSliderWidth - (singleSlideWidth + 2 * remToPxValue)) / 2;
  }, [singleSlideWidth, visibleSliderWidth, margin]);

  // set value for animation property
  // for first active slide, shifts max to left - set to 0
  // for last active slide shift max to right - set to -sliderConstraints
  // for others, use just once offsetFirstElement to center second element
  // and add multiplied values of gap+slideWidth to center each next element
  const outputRadio = useMemo(() => {
    if (activeSlide === 0) {
      return 0;
    } else if (activeSlide >= childrenCount) {
      return -sliderConstraints;
    } else {
      return -offsetFirstElement - (activeSlide - 1) * (singleSlideWidth + remToPxValue);
    }
  }, [activeSlide, childrenCount, sliderConstraints, singleSlideWidth, margin]);

  // on dragging change dynamically active indicator under Slider
  // amount is equally divided by full slider width and amount of children
  // just in case check if childrenCount is not -1, to not divide by 0
  const onDrag = useCallback(() => {
    let amount = 1;
    if (childrenCount !== -1) {
      amount = trueSliderWidth / (childrenCount + 1);
    }
    if (x.get() < -sliderConstraints + remToPxValue * 2) {
      setActiveSlide(childrenCount);
      return;
    }
    setActiveSlide(Math.abs(Math.floor(x.get() / amount)));
  }, [
    trueSliderWidth,
    childrenCount,
    sliderConstraints,
    margin,
    childrenCount,
  ]);

  // similar to onDrag, but sometimes elements are shifting after dragging
  // after 3 seconds calculate current X and check current position
  const onDragEnd = useCallback(() => {
    let amount = 1;
    if (childrenCount !== -1) {
      amount = trueSliderWidth / (childrenCount + 1);
    }
    setTimeout(() => {
      if (x.get() < -sliderConstraints + remToPxValue * 2) {
        setActiveSlide(childrenCount);
        return;
      }
      setActiveSlide(Math.abs(Math.floor(x.get() / amount)));
    }, 3000);
  }, [
    trueSliderWidth,
    childrenCount,
    sliderConstraints,
    margin,
    childrenCount,
  ]);

  return { outputRadio, onDrag, onDragEnd };
};
